export type CardFramingBackgroundType = 'image' | 'color' | 'transparent';

export interface CardFramingArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CardFramingAreaPercentagePreset {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CardFramingSettings {
  background_type: CardFramingBackgroundType;
  background_color: string;
  background_image_url?: string;
  background_image_name?: string;
  background_image_width?: number;
  background_image_height?: number;
  content_area: CardFramingArea;
}

export const DEFAULT_CARD_FRAMING_AREA: CardFramingArea = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
};

const CARD_FRAMING_AREA_DEFAULTS_BY_BACKGROUND_NAME: Record<string, CardFramingAreaPercentagePreset> = {
  'keshet-16x9': { x: 18.3, y: 75.02, w: 81.5, h: 24.5 },
  'תאגיד - כאן חינוכית': { x: 23.0, y: 81.5, w: 58.5, h: 16.2 },
  'תאגיד - מכאן': { x: 1.7, y: 80.5, w: 78.0, h: 17.5 },
  'תאגיד - משותף כאן-מכאן': { x: 1.7, y: 80.5, w: 78.0, h: 17.5 },
  'תאגיד - משותף כאן-מכאן דוקו': { x: 1.7, y: 80.5, w: 78.0, h: 17.5 },
  'תאגיד - משותף מכאן-חינוכית': { x: 1.7, y: 80.5, w: 78.0, h: 17.5 },
  'תאגיד - כאן 11': { x: 1.7, y: 80.5, w: 78.0, h: 17.5 },
};

export const DEFAULT_CARD_FRAMING: CardFramingSettings = {
  background_type: 'transparent',
  background_color: '#000000',
  content_area: DEFAULT_CARD_FRAMING_AREA,
};

export function clampProportion(value: unknown, fallback: number): number {
  const numericValue: number = typeof value === 'number'
    ? value
    : typeof value === 'string'
      ? Number(value)
      : NaN;

  if (!Number.isFinite(numericValue)) return fallback;
  return Math.min(1, Math.max(0, numericValue));
}

export function normalizeCardFramingArea(value: unknown): CardFramingArea {
  const area = value && typeof value === 'object'
    ? value as Partial<CardFramingArea>
    : {};

  const x: number = clampProportion(area.x, DEFAULT_CARD_FRAMING_AREA.x);
  const y: number = clampProportion(area.y, DEFAULT_CARD_FRAMING_AREA.y);
  const width: number = Math.min(
    1 - x,
    clampProportion(area.width, DEFAULT_CARD_FRAMING_AREA.width)
  );
  const height: number = Math.min(
    1 - y,
    clampProportion(area.height, DEFAULT_CARD_FRAMING_AREA.height)
  );

  return { x, y, width, height };
}

export function getDefaultCardFramingAreaForBackgroundName(
  backgroundName: unknown
): CardFramingArea | undefined {
  if (typeof backgroundName !== 'string' || !backgroundName.trim()) {
    return undefined;
  }

  const preset: CardFramingAreaPercentagePreset | undefined =
    CARD_FRAMING_AREA_DEFAULTS_BY_BACKGROUND_NAME[backgroundName];
  if (!preset) {
    return undefined;
  }

  return normalizeCardFramingArea({
    x: preset.x / 100,
    y: preset.y / 100,
    width: preset.w / 100,
    height: preset.h / 100,
  });
}

function normalizeBackgroundType(value: unknown): CardFramingBackgroundType {
  return value === 'image' || value === 'color' || value === 'transparent'
    ? value
    : DEFAULT_CARD_FRAMING.background_type;
}

function toAbsoluteAssetUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    return new URL(value.replace(/\\/g, '/'), window.location.origin).href;
  } catch {
    return value;
  }
}

export function parseCardFramingSettings(value: unknown): CardFramingSettings {
  if (!value) {
    return normalizeCardFramingSettings({});
  }

  if (typeof value === 'string') {
    try {
      return normalizeCardFramingSettings(JSON.parse(value));
    } catch {
      return normalizeCardFramingSettings({});
    }
  }

  return normalizeCardFramingSettings(value);
}

export function normalizeCardFramingSettings(value: unknown): CardFramingSettings {
  const settings = value && typeof value === 'object'
    ? value as Partial<CardFramingSettings>
    : {};

  return {
    background_type: normalizeBackgroundType(settings.background_type),
    background_color: typeof settings.background_color === 'string' && settings.background_color
      ? settings.background_color
      : DEFAULT_CARD_FRAMING.background_color,
    background_image_url: toAbsoluteAssetUrl(settings.background_image_url),
    background_image_name: typeof settings.background_image_name === 'string'
      ? settings.background_image_name
      : undefined,
    background_image_width: Number.isFinite(settings.background_image_width)
      ? settings.background_image_width
      : undefined,
    background_image_height: Number.isFinite(settings.background_image_height)
      ? settings.background_image_height
      : undefined,
    content_area: normalizeCardFramingArea(settings.content_area),
  };
}
